import React, { useEffect, useMemo, useState } from "react";
import "../../client/Client.css";
import "./Candidate.css";
import AddCandidate from "./AddCandidate";
import CandidateFilter from "../CandidateFilter";
import config from "../../../config";
import { CiCirclePlus } from "react-icons/ci";
import AddOnboarding from "../Onboarding/AddOnboarding";
import Header from "../../../components/common/Header";
import { BsSliders } from "react-icons/bs";
import { FiSearch } from "react-icons/fi";



const Candidate = ({ setActivePage }) => {
  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });

  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = (localStorage.getItem("role") || "GUEST").toUpperCase();
    const orgId = localStorage.getItem("orgId") || "";
    setUserDetails({
      name: `${firstName} ${lastName}`.trim(),
      role,
      token,
      orgId,
    });
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [subVendors, setSubVendors] = useState([]);
  const [filters, setFilters] = useState(null);
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const [candidates, setCandidates] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const [errorMsg, setErrorMsg] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [fromConvert, setFromConvert] = useState(false); // ✅ FLAG
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // --- fetch candidates list ---
  useEffect(() => {
    if (!showFilter) return;

    const token = localStorage.getItem("Authorization");
    const orgId = localStorage.getItem("orgId");

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const fetchClients = fetch(
      `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${orgId}`,
      { headers }
    ).then(res => res.json());

    const fetchQualifications = fetch(
      `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${orgId}?configType=Qualification`,
      { headers }
    ).then(res => res.json());

    const fetchJobRoles = fetch(
      `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${orgId}?configType=JobRole`,
      { headers }
    ).then(res => res.json());

    const fetchSubVendors = fetch(
      `${config.API_BASE_URL}${config.SUBVENDOR_LIST_ENDPOINT}?orgKey=${orgId}`,
      { headers }
    ).then(res => res.json());

    Promise.all([
      fetchClients,
      fetchQualifications,
      fetchJobRoles,
      fetchSubVendors,
    ])
      .then(([clientData, qualData, jobData, subVendorData]) => {
        setClients(
          clientData?.body?.map(c => ({
            label: c.name,
            value: c.clientKey,
          })) || []
        );

        setQualifications(
          qualData?.map(q => ({
            label: q.values,
            value: q.id,
          })) || []
        );

        setJobRoles(
          jobData?.map(j => ({
            label: j.values,
            value: j.id,
          })) || []
        );

        setSubVendors(
          Array.isArray(subVendorData.body)
            ? subVendorData.body.map(s => ({
              label: s.subVendorName,
              value: s.subVendorId,
            }))
            : []
        );
      })
      .catch(err =>
        console.error("Filter dropdown fetch error:", err)
      );
  }, [showFilter]);
  const [totalElements, setTotalElements] = useState(0);

  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    const fetchCandidates = async () => {
      setLoading(true);

      try {
        const params = new URLSearchParams();

        // Search
        if (searchTerm) {
          params.append("q", searchTerm);
        }

        // Status filter
        if (filters?.candidateStatus) {
          params.append("statuses", filters.candidateStatus);
        } else {
          params.append(
            "statuses",
            "INITIAL,INTERVIEWED,ACCEPTED,REJECTED,ONBOARDED"
          );
        }

        params.append("page", (currentPage - 1).toString());
        params.append("size", itemsPerPage.toString());

        // Sort
        if (filters?.sortBy) {
          params.append("sort", filters.sortBy);
        } else {
          params.append("sort", "id,desc");
        }

        params.append("orgKey", userDetails.orgId);

        const url = `${config.API_BASE_URL}${config.CANDIDATES_LIST_ENDPOINT}?${params.toString()}`;

        console.log("API URL:", url);

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${userDetails.token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) throw new Error("Failed to fetch");

        const page = await response.json();
        const list = Array.isArray(page?.content) ? page.content : [];

        const normalized = list.map((c, idx) => ({
          key: c.candidateKey || c.id || `C-${idx + 1}`,
          name: c.name || "",
          email: c.email || "",
          phone: c.phone || "",
          role: c.jobRole || "",
          status: c.status || "",
          _raw: c,
        }));

        setCandidates(normalized);
        setTotalElements(page?.totalElements || normalized.length);
        setError(null);
      } catch (err) {
        console.error(err);
        setCandidates([]);
        setTotalElements(0);
        setError("Could not load candidates.");
      } finally {
        setLoading(false);
      }
    };

    fetchCandidates();
  }, [userDetails.token, userDetails.orgId, searchTerm, filters, refreshTrigger, currentPage]);

  console.log("Candidates loaded:", candidates);
  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter((c) => {
      const phoneFlat = (c.phone || "").replace(/\s+/g, "").toLowerCase();
      return (
        (c.key || "").toLowerCase().includes(q) ||
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.role || "").toLowerCase().includes(q) ||
        (c.status || "").toLowerCase().includes(q) ||
        phoneFlat.includes(q.replace(/\s+/g, ""))
      );
    });
  }, [candidates, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(totalElements / itemsPerPage));
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const pageRows = filtered;

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handleCloseAddCandidate = () => {
    setShowAddCandidate(false);
    setSelectedCandidate(null);
  };

  const handleViewCandidate = async (id) => {
    try {
      setErrorMsg("");

      if (!id) {
        console.warn("No candidate id provided to handleViewCandidate");
        return;
      }
      if (!userDetails.token) {
        console.warn("No token available for candidate GET");
        return;
      }

      const url = `${config.API_BASE_URL}${config.CANDIDATES_LIST_ENDPOINT}/${id}`;
      console.log("Fetching candidate details:", url);

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!res.ok) {
        const text = await res.text();
        console.error("Candidate details error:", text);
        setErrorMsg("Could not load candidate details.");
        return;
      }

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        console.error("Non-JSON response received:", text);
        setErrorMsg("Invalid server response.");
        return;
      }

      const data = await res.json();
      console.log("Candidate details response:", data);

      setSelectedCandidate(data);
      setShowAddCandidate(true);
    } catch (err) {
      console.error("Candidate details fetch exception:", err);
      setErrorMsg("Could not load candidate details.");
    }
  };

  const handleRowClick = (candidate) => {
    const id = candidate._raw?.id != null ? candidate._raw.id : null;

    handleViewCandidate(id);
  };


  if (error) {
    return (
      <div>
        <Header
          title="Candidate List"
          subtitle="Candidate Information"
          onProfileClick={() => setActivePage && setActivePage("Profile")}
        />
        <div className='table-container'>
          <div style={{ color: "red", padding: "16px" }}>{error}</div>
        </div>
      </div>
    );
  }

  if (showAddCandidate) {
    return (
      <AddCandidate
        onCancel={handleCloseAddCandidate}
        selectedCandidate={selectedCandidate}
        onDelete={(deleted) => {
          if (deleted?.id != null) {
            setCandidates((prev) =>
              prev.filter((c) => c._raw?.id !== deleted.id)
            );
          }
          handleCloseAddCandidate();
        }}
        onConvertToOnboarding={(candidate) => {
          setSelectedCandidate(candidate);
          setFromConvert(true);
          setShowAddCandidate(false);
          setShowOnboarding(true);
        }}
        onSave={() => setRefreshTrigger((prev) => prev + 1)}
      />
    );
  }

  if (showOnboarding) {
    return (
      <AddOnboarding
        selectedCandidate={selectedCandidate}
        fromConvert={fromConvert}
        onCancel={() => {
          setShowOnboarding(false);
          setShowAddCandidate(true);
          setFromConvert(false);
        }}
        onSave={() => {
          if (setActivePage) {
            setActivePage("Onboarding");
          } else {
            setShowOnboarding(false);
            setRefreshTrigger((prev) => prev + 1);
          }
        }}
      />
    );
  }

  return (
    <div>
      {/* Header */}
      <Header
        title="Candidate List"
        subtitle="Candidate Information"
        onProfileClick={() => setActivePage && setActivePage("Profile")}
      />

      {/* Table card */}
      <div className='table-container'>
        {errorMsg && (
          <div style={{ color: "red", paddingBottom: "8px" }}>{errorMsg}</div>
        )}

        {/* Controls */}
        <div className='table-controls'>
          <div
            className="search-wrapper"
            style={{ position: "relative", display: "flex", alignItems: "center" }}
          >
            <FiSearch
              style={{
                position: "absolute",
                left: "15px",
                fontSize: "15px",
                color: "#999",
              }}
            />

            <input
              type="text"
              className="search-input"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              style={{
                paddingLeft: "40px",
                width: "145px",
                height: "15px",
              }}
            />
          </div>
          <div className='button-container'>
            <button
              className='add-client-btn'
              onClick={() => {
                setSelectedCandidate(null);
                setShowAddCandidate(true);
              }}
            >
              <CiCirclePlus />
              Add Candidate
            </button>
            <button className='filter-btn' onClick={() => setShowFilter(true)}>
              <BsSliders /> Filter
            </button>
          </div>
        </div>

        <CandidateFilter
          open={showFilter}
          onClose={() => setShowFilter(false)}
          clientOptions={clients}
          qualificationOptions={qualifications}
          jobRoleOptions={jobRoles}
          subVendorOptions={subVendors}
          onApply={(data) => {
            setFilters(data);
            setCurrentPage(1);
          }}
          onClear={() => {
            setFilters(null);
            setSearchTerm("");
          }}
        />

        {/* Data table */}
        <table className='table'>
          <thead>
            <tr>
              <th className='th'>Candidate Name</th>
              <th className='th'>Email</th>
              <th className='th'>Phone Number</th>
              <th className='th'>Job Role</th>
              <th className='th'>Candidate Key</th>
              <th className='th'>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className='td' colSpan={6} style={{ textAlign: "center", padding: "20px" }}>
                  Loading candidate data...
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td className='td' colSpan={6} style={{ textAlign: "center" }}>
                  No candidates found
                </td>
              </tr>
            ) : (
              pageRows.map((c) => (
                <tr
                  key={c.key}
                  onClick={() => handleRowClick(c)}
                  style={{ cursor: "pointer" }}
                >
                  <td className='td'>{c.name}</td>
                  <td className='td'>{c.email}</td>
                  <td className='td'>{c.phone}</td>
                  <td className='td'>{c.role}</td>
                  <td className='td'>{c.key}</td>
                  <td className='td'>
                    <span
                      className={`status-badge ${["initial", "interviewed", "interview", "hold", "onboarding", "onboarded"].includes((c.status || "").toLowerCase())
                          ? "active"
                          : ["accepted"].includes((c.status || "").toLowerCase())
                            ? "orange"
                            : "inactive"
                        }`}
                      style={{ textTransform: "capitalize" }}
                    >
                      {c.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Footer / Pagination */}
        <div className='table-footer'>
          <div className='record-count'>
            Showing {totalElements === 0 ? 0 : indexOfFirstItem + 1} to{" "}
            {Math.min(indexOfLastItem, totalElements)} of {totalElements}{" "}
            entries
          </div>

          <div className='pagination'>
            <button
              className='pagination-button'
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              &lt;
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`pagination-button ${currentPage === i + 1 ? "active" : ""
                  }`}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            <button
              className='pagination-button'
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              &gt;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Candidate;
